/**
 * Katalog Ürünleri — tip tanımları.
 *
 * Katalog global bir referans listesidir: ana GTS veritabanında durur, tenant'a bağlı
 * değildir ve FİYAT BİLGİSİ TAŞIMAZ. Kullanıcı katalogdan ürün eklerken satış fiyatını
 * kendi stoğunda belirler.
 *
 * DİKKAT: backend `DefaultIgnoreCondition = WhenWritingNull` ile serileştiriyor; null
 * alanlar JSON'a hiç yazılmaz. Bu yüzden boş olabilen alanlar opsiyonel işaretli.
 */

/** GET /products/paged ve /products/{id} satırı (CatalogController.MapProduct). */
export interface CatalogProduct {
  id: number;
  name: string;
  barcode?: string | null;
  catalogCode?: string | null;
  sectorId: number;
  sectorName?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  parentCategoryId?: number | null;
  parentCategoryName?: string | null;
  /** "Atıştırmalık > Cips" — üst ve alt kategori tek metinde. */
  categoryPath?: string | null;
  vatRate: number;
  vatStatus: string;
  unit: string;
  departmentNo?: number | null;
  imageUrl?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

/** GET /sectors satırı. productCount yalnızca includeCounts=true iken doludur. */
export interface CatalogSector {
  id: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
}

/** GET /categories satırı. parentId boşsa üst kategori, doluysa alt kategori. */
export interface CatalogCategory {
  id: number;
  sectorId: number;
  parentId?: number | null;
  name: string;
  sortOrder: number;
  isActive: boolean;
  productCount: number;
}

/** GET /products/summary — istatistik kartlarını besler. */
export interface CatalogSummary {
  totalProducts: number;
  activeProducts: number;
  sectorCount: number;
  categoryCount: number;
  /** Kategorisi atanmamış veya "Sınıflandırılamadı" sektöründeki ürünler. */
  unclassifiedProducts: number;
}

/** Sunucuya gidecek filtre/sayfalama parametreleri. */
export interface CatalogProductQuery {
  /** 1-tabanlı; MUI 0-tabanlı tuttuğu için istekte page + 1 gönderilir. */
  page: number;
  pageSize: number;
  search?: string;
  sectorId?: number;
  categoryId?: number;
  unit?: string;
  vatRate?: number;
  isActive?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

/** Summary ucu sayfalama/sıralama almaz; listeyle aynı filtreleri paylaşır. */
export type CatalogSummaryQuery = Omit<
  CatalogProductQuery,
  'page' | 'pageSize' | 'sortColumn' | 'sortDirection'
>;

export interface CatalogPagedResult {
  items: CatalogProduct[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type CatalogStatusFilter = 'all' | 'active' | 'inactive';

/**
 * Filtre diyaloğunun taslak durumu. Kullanıcı "Uygula"ya basana kadar ana state'e
 * yazılmaz; InventoryFilters'taki desenin aynısı.
 */
export interface CatalogFilterDraft {
  sector: CatalogSector | null;
  category: CatalogCategory | null;
  unit: string | null;
  vatRate: number | null;
  status: CatalogStatusFilter;
}

export const emptyCatalogFilterDraft = (): CatalogFilterDraft => ({
  sector: null,
  category: null,
  unit: null,
  vatRate: null,
  status: 'all',
});

/**
 * Katalogdaki birimler. Backend'de birim listesi ucu yok; seed verisindeki dağılım
 * Adet 23.029 · Kg 151 · Koli 5 · Teneke 1. Yeni birim eklenirse bu liste genişletilir.
 */
export const CATALOG_UNIT_OPTIONS = ['Adet', 'Kg', 'Koli', 'Teneke'] as const;

/** Katalogda kullanılan KDV oranları (seed: 0 / 1 / 10 / 20). */
export const CATALOG_VAT_RATE_OPTIONS = [0, 1, 8, 10, 18, 20];

/** KDV durumu metinleri — seed bu yazımı kullanıyor, backend varsayılanı "Kdv Dahil". */
export const CATALOG_VAT_STATUS_OPTIONS = ['Kdv Dahil', 'Kdv Hariç'];

/**
 * Ürün oluşturma/güncelleme gövdesi (POST /products/create ve /products/update).
 * `id` yalnızca güncellemede dolar. FİYAT ALANI BİLEREK YOK.
 */
export interface CatalogProductPayload {
  id?: number;
  name: string;
  barcode?: string | null;
  catalogCode?: string | null;
  sectorId: number;
  categoryId?: number | null;
  vatRate?: number;
  vatStatus?: string;
  unit?: string;
  departmentNo?: number | null;
  imageUrl?: string | null;
  isActive?: boolean;
}

/** Toplu işlem uçlarının ortak yanıtı. */
export interface CatalogBulkResult {
  message: string;
  affected: number;
}

/** /products/bulk-create içinde kaydedilemeyen tek satır. */
export interface CatalogBulkFailure {
  name: string;
  barcode?: string | null;
  reason: string;
}

/** /products/bulk-create yanıtı. */
export interface CatalogBulkCreateResponse {
  message: string;
  successCount: number;
  failedCount: number;
  failedItems: CatalogBulkFailure[];
}

/** "Tabloyu özelleştir" ayarı — hangi sütunun görüneceği localStorage'da saklanır. */
export interface CatalogColumnSetting {
  id: string;
  label: string;
  visible: boolean;
  /** Sabit sütunlar gizlenemez (sıra no, ürün adı, işlemler). */
  fixed: boolean;
}

export const CATALOG_COLUMN_STORAGE_KEY = 'superadmin-catalog-columns';

/** Sütunların varsayılan görünürlüğü. Sıra bu dizideki sırayla aynıdır. */
export const DEFAULT_CATALOG_COLUMNS: CatalogColumnSetting[] = [
  { id: 'index', label: 'Sıra No', visible: true, fixed: true },
  { id: 'name', label: 'Ürün Adı', visible: true, fixed: true },
  { id: 'barcode', label: 'Barkod', visible: true, fixed: false },
  { id: 'catalogCode', label: 'Katalog Kodu', visible: true, fixed: false },
  { id: 'sectorName', label: 'Sektör', visible: true, fixed: false },
  { id: 'categoryPath', label: 'Kategori', visible: true, fixed: false },
  { id: 'unit', label: 'Birim', visible: true, fixed: false },
  { id: 'vatRate', label: 'KDV Oranı', visible: true, fixed: false },
  { id: 'vatStatus', label: 'KDV Durumu', visible: false, fixed: false },
  { id: 'departmentNo', label: 'ÖKC Departman', visible: false, fixed: false },
  { id: 'isActive', label: 'Durum', visible: true, fixed: false },
  { id: 'actions', label: 'İşlemler', visible: true, fixed: true },
];
